import { useState, useEffect } from 'react';
import api from '../api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Crown, Star, Check, Loader2, Sparkles, Gift, Percent, Clock, Key, ArrowRight } from 'lucide-react';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });

export default function VIPPage() {
  const { user, fetchUser } = useAuthStore();
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [redeemKey, setRedeemKey] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => { fetchPlans(); }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/vip/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch VIP plans:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async (planId) => {
    setPurchasing(planId);
    try {
      await api.post('/vip/purchase', { plan_id: planId });
      toast.success('VIP subscription activated!');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase VIP');
    } finally {
      setPurchasing(null);
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!redeemKey.trim()) return;
    
    setRedeeming(true);
    try {
      const response = await api.post('/vip/redeem', { key: redeemKey.trim() });
      toast.success(response.data.message);
      setRedeemKey('');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or used key');
    } finally {
      setRedeeming(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Crown className="w-10 h-10 text-[#FFD700]" />
          <h1 className="text-3xl font-bold text-white font-['Outfit']">VIP Membership</h1>
        </div>
        <p className="text-zinc-400 max-w-lg mx-auto">
          Unlock exclusive benefits, special discounts, and priority support with our VIP membership plans
        </p>
      </div>

      {/* Current VIP Status */}
      {user?.is_vip && (
        <Card className="bg-gradient-to-r from-[#FFD700]/20 to-[#FFD700]/5 border-2 border-[#FFD700]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#FFD700]/30 flex items-center justify-center">
                  <Crown className="w-8 h-8 text-[#FFD700]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#FFD700]">You are a VIP Member!</h2>
                  <p className="text-zinc-400">Plan: {user.vip_plan_name}</p>
                </div>
              </div>
              <div className="text-right">
                <Badge className="bg-[#FFD700] text-black font-bold px-4 py-1">
                  <Crown className="w-4 h-4 mr-1" />ACTIVE
                </Badge>
                <p className="text-sm text-zinc-400 mt-2 flex items-center gap-1 justify-end">
                  <Clock className="w-4 h-4" />
                  Expires: {formatDate(user.vip_expires_at)}
                </p>
              </div>
            </div>
            
            {user.vip_discount_percent > 0 && (
              <div className="mt-4 p-4 bg-[#FFD700]/10 rounded-lg flex items-center gap-3">
                <Percent className="w-6 h-6 text-[#FFD700]" />
                <span className="text-white">
                  You have <strong className="text-[#FFD700]">{user.vip_discount_percent}% discount</strong> on all products!
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Redeem VIP Key */}
      <Card className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Key className="w-5 h-5 text-[#FFD700]" />
            Have a VIP Key?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRedeem} className="flex gap-3 flex-wrap">
            <Input
              value={redeemKey}
              onChange={(e) => setRedeemKey(e.target.value.toUpperCase())}
              placeholder="Enter your VIP key (e.g., XXXX-XXXX-XXXX-XXXX)"
              className="bg-[#121212] border-white/10 text-white flex-1 min-w-[250px] font-mono uppercase"
              maxLength={19}
            />
            <Button 
              type="submit" 
              disabled={redeeming || !redeemKey.trim()}
              className="bg-[#FFD700] text-black hover:bg-[#E5C100]"
            >
              {redeeming ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Redeeming...</>
              ) : (
                <><Key className="w-4 h-4 mr-2" />Redeem Key</>
              )}
            </Button>
          </form>
          <p className="text-zinc-500 text-sm mt-2">
            {user?.is_vip 
              ? 'Redeeming a key will extend your current VIP membership'
              : 'Enter your VIP key to activate your membership instantly'}
          </p>
        </CardContent>
      </Card>

      {/* VIP Plans */}
      {plans.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-12 text-center">
            <Crown className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">No VIP plans available at the moment</p>
            <p className="text-zinc-500 text-sm mt-2">Check back soon or use a VIP key if you have one!</p>
          </CardContent>
        </Card>
      ) : (
        <div>
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#FFD700]" />
            Purchase VIP Membership
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan, idx) => {
              const isPopular = idx === 1 || plan.name.toLowerCase().includes('month');
              const isTrial = plan.duration_days <= 7;
              
              return (
                <Card 
                  key={plan.id} 
                  className={`bg-[#0A0A0A] border-2 relative overflow-hidden ${
                    isPopular ? 'border-[#FFD700] scale-105' : isTrial ? 'border-green-500/50' : 'border-white/10'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-[#FFD700] text-black text-center text-sm font-bold py-1">
                      MOST POPULAR
                    </div>
                  )}
                  {isTrial && !isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-green-500 text-white text-center text-sm font-bold py-1">
                      TRIAL
                    </div>
                  )}
                  
                  <CardHeader className={isPopular || isTrial ? 'pt-10' : ''}>
                    <CardTitle className="flex items-center gap-2 text-white">
                      <Crown className={`w-6 h-6 ${isPopular ? 'text-[#FFD700]' : isTrial ? 'text-green-500' : 'text-zinc-400'}`} />
                      {plan.name}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-white">{formatMoney(plan.price)}</span>
                      </div>
                      <p className="text-zinc-500">{plan.duration_days} days access</p>
                    </div>
                    
                    {plan.discount_percent > 0 && (
                      <Badge className="bg-green-500/20 text-green-500 text-sm">
                        <Percent className="w-4 h-4 mr-1" />
                        {plan.discount_percent}% off all products
                      </Badge>
                    )}
                    
                    <ul className="space-y-2">
                      {plan.benefits?.map((benefit, bidx) => (
                        <li key={bidx} className="flex items-center gap-2 text-zinc-300">
                          <Check className="w-4 h-4 text-[#FFD700]" />
                          {benefit}
                        </li>
                      ))}
                      <li className="flex items-center gap-2 text-zinc-300">
                        <Check className="w-4 h-4 text-[#FFD700]" />
                        Priority customer support
                      </li>
                      <li className="flex items-center gap-2 text-zinc-300">
                        <Check className="w-4 h-4 text-[#FFD700]" />
                        Exclusive VIP badge
                      </li>
                    </ul>
                    
                    <Button
                      onClick={() => handlePurchase(plan.id)}
                      disabled={purchasing === plan.id}
                      className={`w-full ${
                        isPopular 
                          ? 'bg-[#FFD700] text-black hover:bg-[#E5C100]' 
                          : isTrial
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {purchasing === plan.id ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" />{isTrial ? 'Start Trial' : 'Get VIP Access'}</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Benefits Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-white text-center mb-8">VIP Benefits</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-[#0A0A0A] border-white/10 text-center">
            <CardContent className="p-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#FFD700]/20 flex items-center justify-center mb-4">
                <Percent className="w-8 h-8 text-[#FFD700]" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Exclusive Discounts</h3>
              <p className="text-zinc-400 text-sm">Get up to 20% off on all products with your VIP membership</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 text-center">
            <CardContent className="p-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center mb-4">
                <Star className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Priority Support</h3>
              <p className="text-zinc-400 text-sm">Get faster responses and dedicated support as a VIP member</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 text-center">
            <CardContent className="p-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <Gift className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Special Offers</h3>
              <p className="text-zinc-400 text-sm">Access to exclusive products and special promotional offers</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
